/**
 * DataTable Component
 *
 * A styled data table using MUI DataGridPro with design system styling.
 * Features clean headers, link-styled cells, status badges, and pagination.
 */

import React, { useMemo, useCallback } from 'react';
import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import { Box, Typography } from '@mui/material';
import type {
    DataGridProProps,
    GridColDef,
    GridPaginationModel,
    GridValidRowModel,
    GridColumnOrderChangeParams,
} from '@mui/x-data-grid-pro';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface DataTableColumn<R extends GridValidRowModel = any> extends Omit<GridColDef<R>, 'field'> {
    field: string;
}

export interface DataTableProps<R extends GridValidRowModel = any>
    extends Omit<DataGridProProps<R>, 'columns' | 'rows' | 'onColumnOrderChange'> {
    /** Table rows */
    rows: R[];
    /** Column definitions */
    columns: DataTableColumn<R>[];
    /** Unique row ID getter */
    getRowId: (row: R) => string | number;
    /** Total row count for pagination */
    totalRows?: number;
    /** Current page (1-indexed) */
    page?: number;
    /** Page size */
    pageSize?: number;
    /** Pagination callback */
    onPageChange?: (page: number) => void;
    /** Whether to show pagination */
    showPagination?: boolean;
    /** Loading state */
    loading?: boolean;
    /** Height of the table */
    height?: number | string;
    /** Info text shown in footer (e.g., "Showing 1-12 of 89 systems") */
    infoText?: string;
    /** Callback when column order changes via drag-and-drop (returns array of field names) */
    onColumnOrderChange?: (newOrder: string[]) => void;
}

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 48;

// Custom styles for the data grid
const dataGridSx = {
    border: 'none',
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.sm,

    // Header styles
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: colors.neutral[50],
        borderBottom: `1px solid ${colors.neutral[200]}`,
        minHeight: `${HEADER_HEIGHT}px !important`,
        maxHeight: `${HEADER_HEIGHT}px !important`,
    },
    '& .MuiDataGrid-columnHeader': {
        padding: '0 16px',
        '&:focus, &:focus-within': {
            outline: 'none',
        },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: typographyTokens.fontSize.xs,
        fontWeight: typographyTokens.fontWeight.semibold,
        color: colors.neutral[500],
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    '& .MuiDataGrid-columnSeparator': {
        display: 'none',
    },
    '& .MuiDataGrid-sortIcon': {
        color: colors.neutral[400],
        fontSize: 16,
    },

    // Row styles
    '& .MuiDataGrid-row': {
        borderBottom: `1px solid ${colors.neutral[100]}`,
        '&:hover': {
            backgroundColor: colors.neutral[50],
        },
        '&.Mui-selected': {
            backgroundColor: `${colors.primary[50]} !important`,
        },
    },

    // Cell styles
    '& .MuiDataGrid-cell': {
        padding: '0 16px',
        borderBottom: 'none',
        fontSize: typographyTokens.fontSize.sm,
        color: colors.neutral[700],
        '&:focus, &:focus-within': {
            outline: 'none',
        },
    },

    // Footer/pagination styles
    '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${colors.neutral[200]}`,
        backgroundColor: colors.neutral[50],
        minHeight: '52px',
    },
    '& .MuiTablePagination-root': {
        color: colors.neutral[600],
        fontSize: typographyTokens.fontSize.sm,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: typographyTokens.fontSize.sm,
        color: colors.neutral[600],
    },
    '& .MuiTablePagination-actions': {
        '& .MuiIconButton-root': {
            color: colors.neutral[600],
            border: `1px solid ${colors.neutral[300]}`,
            borderRadius: `${radii.md}px`,
            margin: '0 2px',
            padding: '4px',
            '&:hover': {
                backgroundColor: colors.neutral[100],
            },
            '&.Mui-disabled': {
                color: colors.neutral[300],
                borderColor: colors.neutral[200],
            },
        },
    },

    // Remove cell focus outline
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none !important',
    },

    // Virtual scroller - ensure horizontal scroll works
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: colors.neutral[0],
        overflowX: 'auto',
    },

    // Main container needs to allow horizontal scroll
    '& .MuiDataGrid-main': {
        overflow: 'auto',
    },

    // Overlay
    '& .MuiDataGrid-overlay': {
        backgroundColor: `${colors.neutral[0]}CC`,
    },
};

// Custom footer component
interface CustomFooterProps {
    infoText?: string;
    showPagination?: boolean;
}

const CustomFooter: React.FC<CustomFooterProps> = ({ infoText, showPagination = true }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderTop: `1px solid ${colors.neutral[200]}`,
                backgroundColor: colors.neutral[50],
                minHeight: 52,
            }}>
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.sm,
                    color: colors.neutral[600],
                }}>
                {infoText || ''}
            </Typography>
            {showPagination && <GridPagination />}
        </Box>
    );
};

export const DataTable = <R extends GridValidRowModel>({
    rows,
    columns,
    getRowId,
    totalRows,
    page = 1,
    pageSize = 12,
    onPageChange,
    showPagination = true,
    loading = false,
    height = 600,
    infoText,
    onColumnOrderChange,
    ...props
}: DataTableProps<R>) => {
    const paginationModel: GridPaginationModel = useMemo(
        () => ({
            page: page - 1,
            pageSize,
        }),
        [page, pageSize]
    );

    const handlePaginationChange = useCallback(
        (model: GridPaginationModel) => {
            onPageChange?.(model.page + 1);
        },
        [onPageChange]
    );

    const handleColumnOrderChange = useCallback(
        (params: GridColumnOrderChangeParams) => {
            if (onColumnOrderChange) {
                const currentOrder = columns.map((col) => col.field);
                const newOrder = [...currentOrder];
                // Move the column from oldIndex to targetIndex
                const [moved] = newOrder.splice(params.oldIndex, 1);
                newOrder.splice(params.targetIndex, 0, moved);
                onColumnOrderChange(newOrder);
            }
        },
        [columns, onColumnOrderChange]
    );

    // Create footer component with current props
    const FooterComponent = useMemo(() => {
        return function Footer() {
            return <CustomFooter infoText={infoText} showPagination={showPagination} />;
        };
    }, [infoText, showPagination]);

    return (
        <Box
            sx={{
                height,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                flex: height === '100%' ? 1 : undefined,
            }}>
            <DataGridPro
                rows={rows}
                columns={columns as GridColDef[]}
                getRowId={getRowId}
                loading={loading}
                pagination={showPagination}
                paginationMode='server'
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                onColumnOrderChange={onColumnOrderChange ? handleColumnOrderChange : undefined}
                pageSizeOptions={[pageSize]}
                rowCount={totalRows ?? rows.length}
                rowHeight={ROW_HEIGHT}
                columnHeaderHeight={HEADER_HEIGHT}
                disableColumnMenu
                disableColumnResize
                disableRowSelectionOnClick
                disableDensitySelector
                sx={{ ...dataGridSx, flex: 1 }}
                slots={{
                    footer: FooterComponent,
                }}
                slotProps={{
                    pagination: {
                        showFirstButton: true,
                        showLastButton: true,
                    },
                }}
                {...props}
            />
        </Box>
    );
};

export default DataTable;
